import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { FaBars, FaBell, FaSignOutAlt, FaChevronDown, FaUser, FaCog } from 'react-icons/fa';
import IconWrapper from './IconWrapper';
import apiService from '../../services/api';
import socketService from '../../services/socketService';

interface NavbarProps {
  toggleSidebar: () => void;
}

// Animations
const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const NavbarContainer = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid #f1f3f4;
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(10px);
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: #141340;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  border-radius: 12px;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    color: #F68B21;
    transform: scale(1.1);
  }
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #6c757d;
  
  @media (max-width: 576px) {
    display: none;
  }
`;

const NavbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const NotificationButton = styled.button`
  background: none;
  border: none;
  color: #495057;
  font-size: 1.1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  border-radius: 12px;
  position: relative;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    color: #F68B21;
    transform: scale(1.1);
  }
`;

const NotificationBadge = styled.span<{ count: number }>`
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 0.7rem;
  font-weight: 600;
  display: ${({ count }) => (count > 0 ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  animation: ${pulse} 2s infinite;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.4);
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  position: relative;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 12px;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  }
  
  @media (max-width: 576px) {
    .user-details {
      display: none;
    }
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #141340 0%, #1a1a4a 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  box-shadow: 0 4px 12px rgba(20, 19, 64, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(20, 19, 64, 0.4);
  }
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const UserName = styled.span`
  font-weight: 600;
  color: #141340;
  font-size: 0.9rem;
`;

const UserRole = styled.span`
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: 500;
`;

const DropdownArrow = styled.div`
  color: #6c757d;
  font-size: 0.8rem;
  transition: all 0.3s ease;
  
  &.open {
    transform: rotate(180deg);
  }
`;

const UserDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  border: 1px solid #f1f3f4;
  min-width: 200px;
  z-index: 1000;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transform: translateY(${({ isOpen }) => (isOpen ? '0' : '-10px')});
  transition: all 0.3s ease;
  animation: ${({ isOpen }) => (isOpen ? slideDown : 'none')} 0.3s ease-out;
  margin-top: 0.5rem;
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  text-align: left;
  color: #495057;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  
  &:first-child {
    border-radius: 12px 12px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 12px 12px;
  }
  
  &:hover {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    color: #F68B21;
  }
`;

const Separator = styled.div`
  height: 1px;
  background: #f1f3f4;
  margin: 0.5rem 0;
`;

const LogoutButton = styled(DropdownItem)`
  color: #dc3545;
  
  &:hover {
    background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
    color: #721c24;
  }
`;

const Navbar: React.FC<NavbarProps> = ({ toggleSidebar }) => {
  const { user, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    const loadUnreadCount = async () => {
      try {
        if (user) {
          // Load unread notifications count from real API
          const response = await apiService.getNotifications({ limit: 1 });
          setUnreadCount(response.unreadCount || 0);
        }
      } catch (error) {
        console.error('Error loading unread notifications count:', error);
        setUnreadCount(0);
      }
    };
    
    if (user) {
      loadUnreadCount();
    }
  }, [user]);

  // Real-time notification updates
  useEffect(() => {
    if (user) {
      // Listen for new notifications
      socketService.onNotification((data) => {
        console.log('New notification received:', data);
        setUnreadCount(data.unreadCount || 0);
      });

      // Listen for read all notifications
      socketService.onNotificationReadAll((data) => {
        console.log('All notifications marked as read:', data);
        setUnreadCount(0);
      });

      // Cleanup listeners on unmount
      return () => {
        socketService.offNotification();
        socketService.offNotificationReadAll();
      };
    }
  }, [user]);
  
  // Generate breadcrumb based on current path
  const generateBreadcrumb = () => {
    const pathSegments = location.pathname.split('/').filter(segment => segment);
    
    if (pathSegments.length === 0) {
      return { main: 'Dashboard', sub: 'Overview' };
    }
    
    const routeMap: { [key: string]: string } = {
      'dashboard': 'Dashboard',
      'projects': 'Projects',
      'funds': 'Fund Requests',
      'procurement': 'Procurement',
      'reports': 'Reports',
      'users': 'Staff Management',
      'office': 'Office Management',
      'notifications': 'Notifications',
      'logs': 'System Logs',
      'new': 'New',
      'edit': 'Edit',
      'finance': 'Finance',
      'loans': 'Loans',
      'budget': 'Budget'
    };
    
    const main = routeMap[pathSegments[0]] || pathSegments[0];
    
    if (pathSegments.length === 1) {
      if (pathSegments[0] === 'dashboard') {
        return { main: 'Dashboard', sub: 'Overview' };
      }
      return { main, sub: 'List' };
    }
    
    const sub = routeMap[pathSegments[1]] || pathSegments[1];
    return { main, sub };
  };
  
  const breadcrumb = generateBreadcrumb();
  
  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user || !user.name) return '';
    const nameParts = user.name.split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`;
    }
    return nameParts[0][0];
  };
  
  const handleDropdownToggle = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };
  
  const handleLogout = () => {
    setIsDropdownOpen(false);
    logout();
  };

  const handleProfileClick = () => {
    setIsDropdownOpen(false);
    // Navigate based on user
    if (user?.email === 'admin@example.com') {
      navigate('/admin/profile');
    } else if (user?.role === 'HR (Admin)') {
      navigate('/hr/profile');
    } else if (user?.role === 'Project Manager') {
      navigate('/manager/profile');
    } else if (user?.role === 'Supervisor') {
      navigate('/supervisor/profile');
    } else if (user?.role === 'Store-Keeper (Logistics)') {
      navigate('/storekeeper/profile');
    } else {
      navigate('/dashboard'); // Default fallback
    }
  };

  const handleSettingsClick = () => {
    setIsDropdownOpen(false);
    // Navigate based on user
    if (user?.email === 'admin@example.com') {
      navigate('/admin/settings');
    } else if (user?.role === 'HR (Admin)') {
      navigate('/hr/settings');
    } else if (user?.role === 'Project Manager') {
      navigate('/manager/settings');
    } else if (user?.role === 'Supervisor') {
      navigate('/supervisor/settings');
    } else if (user?.role === 'Store-Keeper (Logistics)') {
      navigate('/storekeeper/settings');
    } else {
      navigate('/dashboard'); // Default fallback
    }
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isDropdownOpen && !(event.target as Element).closest('.user-dropdown')) {
        setIsDropdownOpen(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isDropdownOpen]);
  
  return (
    <NavbarContainer>
      <LeftSection>
        <ToggleButton onClick={toggleSidebar}>
          <IconWrapper icon={FaBars} />
        </ToggleButton>
        
        <BreadcrumbContainer>
          <span>{breadcrumb.main}</span>
          <span>/</span>
          <span style={{ color: '#F68B21', fontWeight: '500' }}>{breadcrumb.sub}</span>
        </BreadcrumbContainer>
      </LeftSection>
      
      <NavbarRight>
        <NotificationButton onClick={() => navigate('/notifications')}>
          <IconWrapper icon={FaBell} />
          <NotificationBadge count={unreadCount}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </NotificationBadge>
        </NotificationButton>
        
        <UserSection className="user-dropdown">
          <UserInfo onClick={handleDropdownToggle}>
            <UserAvatar>
              {getUserInitials()}
            </UserAvatar>
            <UserDetails className="user-details">
              <UserName>{user?.name}</UserName>
              <UserRole>{user?.role}</UserRole>
            </UserDetails>
            <DropdownArrow className={isDropdownOpen ? 'open' : ''}>
              <IconWrapper icon={FaChevronDown} />
            </DropdownArrow>
          </UserInfo>
          
          <UserDropdown isOpen={isDropdownOpen}>
            <DropdownItem onClick={handleProfileClick}>
              <IconWrapper icon={FaUser} />
              Profile
            </DropdownItem>
            <DropdownItem onClick={handleSettingsClick}>
              <IconWrapper icon={FaCog} />
              Settings
            </DropdownItem>
            <Separator />
            <LogoutButton onClick={handleLogout}>
              <IconWrapper icon={FaSignOutAlt} />
              Logout
            </LogoutButton>
          </UserDropdown>
        </UserSection>
      </NavbarRight>
    </NavbarContainer>
  );
};

export default Navbar; 